import { useState } from "react";
import { Link } from "react-router-dom";
import { Download, Eye, FileSpreadsheet, FileText, History } from "lucide-react";
import { toast } from "sonner";
import { downloadUrl, formatDate, formatSize } from "@/lib/api";
import { PdfPreviewDialog } from "@/components/PdfPreviewDialog";
import { VersionsDialog } from "@/components/VersionsDialog";
import { CountUp } from "@/components/CountUp";
import { PriceChangedBadge } from "@/components/PriceChangedBadge";
import { ReportDocButton } from "@/components/ReportDocButton";

const DownloadBtn = ({ doc, fmt }) => {
  const file = doc[fmt];
  if (!file) return null;
  const isPdf = fmt === "pdf";
  const Icon = isPdf ? FileText : FileSpreadsheet;
  return (
    <a
      href={downloadUrl(doc.id, fmt)}
      download
      data-testid={`download-${fmt}-${doc.id}`}
      onClick={() => toast.success(`${isPdf ? "PDF" : "Excel"} download started`, { description: `${doc.brand_name} — ${doc.title}` })}
      className={
        isPdf
          ? "inline-flex items-center gap-2 rounded-full bg-ph-lime px-4 py-2.5 text-sm font-semibold text-ph-navy transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_24px_rgba(182,255,92,0.45)]"
          : "inline-flex items-center gap-2 rounded-full border border-ph-violet/40 bg-ph-violet/10 px-4 py-2.5 text-sm font-medium text-ph-ink transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-ph-violet/20"
      }
    >
      <Icon className="h-4 w-4" /> {isPdf ? "PDF" : "Excel"}
      <span className="font-mono-ph text-[11px] opacity-75">{formatSize(file.size)}</span>
      <Download className="h-3.5 w-3.5" />
    </a>
  );
};

export function DocumentCard({ doc, showBrand = true }) {
  const [preview, setPreview] = useState(false);
  const [versions, setVersions] = useState(false);
  const past = doc.history?.length ?? 0;
  return (
    <article
      data-testid={`document-card-${doc.id}`}
      className="group relative flex h-full flex-col rounded-[1.5rem] border border-ph-line bg-ph-surface p-6 transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-1 hover:border-ph-accent/40 hover:shadow-[0_28px_50px_-28px_rgb(var(--ph-glow)/0.4)]"
    >
      <div className="flex items-center gap-2">
        {showBrand && (
          <Link to={`/brands/${doc.brand_slug}`} data-testid={`document-brand-link-${doc.id}`} className="font-mono-ph text-xs font-semibold uppercase tracking-[0.15em] text-ph-accent hover:underline">
            {doc.brand_name}
          </Link>
        )}
        {doc.price_changed ? (
          <PriceChangedBadge testId={`document-price-changed-${doc.id}`} />
        ) : doc.is_new && (
          <span data-testid={`document-new-badge-${doc.id}`} className="rounded-full bg-ph-lime px-2 py-0.5 font-mono-ph text-[10px] font-bold uppercase tracking-wider text-ph-navy">
            New
          </span>
        )}
        {doc.version && <span className="ml-auto rounded-full bg-ph-raised px-2.5 py-0.5 font-mono-ph text-[11px] text-ph-muted">Rev {doc.version}</span>}
        <span className={`${doc.version ? "" : "ml-auto "}rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 font-mono-ph text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300`} data-testid={`document-current-${doc.id}`}>Current</span>
      </div>
      <h3 className="mt-4 font-display text-xl font-bold leading-tight tracking-tight text-ph-ink">{doc.title}</h3>
      <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <dt className="text-ph-muted">Category</dt><dd className="text-right text-ph-ink">{doc.category}</dd>
        <dt className="text-ph-muted">Effective date</dt><dd className="text-right font-mono-ph text-[13px] text-ph-ink">{formatDate(doc.updated_date)}</dd>
        <dt className="text-ph-muted">Uploaded</dt><dd className="text-right font-mono-ph text-[13px] text-ph-ink">{formatDate(doc.replaced_at || doc.created_at)}</dd>
        <dt className="text-ph-muted">Format</dt>
        <dd className="text-right text-ph-ink">{[doc.pdf && `PDF ${formatSize(doc.pdf.size)}`, doc.excel && `Excel ${formatSize(doc.excel.size)}`].filter(Boolean).join(" · ")}</dd>
        <dt className="text-ph-muted">Downloads</dt>
        <dd className="flex items-center justify-end gap-1.5 text-right">
          <Download className="h-3.5 w-3.5 text-ph-accent" />
          <CountUp value={doc.download_count ?? 0} className="font-mono-ph text-[13px] font-semibold text-ph-ink" testId={`document-downloads-${doc.id}`} />
        </dd>
      </dl>
      <div className="mt-auto flex flex-wrap items-center gap-2 pt-6">
        <DownloadBtn doc={doc} fmt="pdf" />
        <DownloadBtn doc={doc} fmt="excel" />
        {doc.pdf && (
          <button
            type="button"
            onClick={() => setPreview(true)}
            data-testid={`preview-pdf-${doc.id}`}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-2.5 text-sm font-medium text-ph-muted transition-colors hover:bg-ph-raised hover:text-ph-ink"
          >
            <Eye className="h-4 w-4" /> Preview
          </button>
        )}
        {past > 0 && (
          <button
            type="button"
            onClick={() => setVersions(true)}
            data-testid={`versions-btn-${doc.id}`}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-2.5 text-sm font-medium text-ph-accent transition-colors hover:bg-ph-raised"
          >
            <History className="h-4 w-4" /> {past} archived {past === 1 ? "version" : "versions"}
          </button>
        )}
        <ReportDocButton doc={doc} />
      </div>
      {doc.pdf && <PdfPreviewDialog doc={doc} open={preview} onOpenChange={setPreview} />}
      {past > 0 && <VersionsDialog doc={doc} open={versions} onOpenChange={setVersions} />}
    </article>
  );
}
